// src/pages/client/ClientConfigPage.jsx
// Client configuration page with dynamic branding from tenant configuration

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.invenire.app';

export default function ClientConfigPage({ tenantKeyFromSubdomain }) {
  const navigate = useNavigate();
  // Get tenantKey from URL params OR from subdomain prop
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  // Determine if we're on subdomain (affects navigation paths)
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  const getAdminPath = () => isSubdomain ? "/admin" : `/client/${tenantKey}`;
  
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3b82f6');
  const [accentColor, setAccentColor] = useState('#10b981');
  const [backgroundColor, setBackgroundColor] = useState('#0d1117');
  const [textColorAuto, setTextColorAuto] = useState(true);
  const [textColor, setTextColor] = useState('#ffffff');
  const [headerGradient, setHeaderGradient] = useState('');
  const [buttonPrimaryBg, setButtonPrimaryBg] = useState('#3b82f6');
  const [buttonTextColor, setButtonTextColor] = useState('#ffffff');
  
  // Interview config
  const [introScript, setIntroScript] = useState('');
  const [endScript, setEndScript] = useState('');
  const [thankHeading, setThankHeading] = useState('');
  const [thankBody, setThankBody] = useState('');

  const loadTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/tenants/${tenantKey}`);
      if (!response.ok) throw new Error('Failed to load tenant');
      
      const data = await response.json();
      setTenant(data);
      
      // Populate form from branding
      const b = data.branding || {};
      const ic = data.interview_config || {};
      
      setCompanyName(b.companyName || data.name || '');
      setLogoUrl(b.logoUrl || '');
      setPrimaryColor(b.primaryColor || '#3b82f6');
      setAccentColor(b.accentColor || '#10b981');
      setBackgroundColor(b.backgroundColor || '#0d1117');
      setTextColorAuto(!b.textColor);
      setTextColor(b.textColor || '#ffffff');
      setHeaderGradient(b.headerGradient || '');
      setButtonPrimaryBg(b.buttonPrimaryBg || '#3b82f6');
      setButtonTextColor(b.buttonTextColor || '#ffffff');
      
      setIntroScript(ic.intro_script || '');
      setEndScript(ic.end_script || '');
      setThankHeading(ic.thank_you_heading || '');
      setThankBody(ic.thank_you_body || '');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantKey]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  // Calculate if background is light or dark
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  }, [backgroundColor]);

  // Auto-calculated text color
  const autoTextColor = isLightBackground ? '#111827' : '#ffffff';
  const effectiveTextColor = textColorAuto ? autoTextColor : textColor;

  // Dynamic styles based on current form values (live preview)
  const styles = useMemo(() => {
    const textPrimary = effectiveTextColor;
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const inputBg = isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.05)';
    const inputBorder = isLightBackground ? '#d1d5db' : 'rgba(255,255,255,0.1)';
    
    return {
      container: {
        minHeight: '100vh',
        backgroundColor: backgroundColor,
        color: textPrimary,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        borderBottom: `1px solid ${cardBorder}`,
      },
      logoContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      },
      logoImage: {
        height: 48,
        width: 'auto',
        objectFit: 'contain',
      },
      logoText: {
        fontSize: 28,
        fontWeight: 700,
        color: textPrimary,
        margin: 0,
      },
      subtitle: {
        fontSize: 14,
        color: textSecondary,
      },
      backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        background: isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.1)',
        color: textPrimary,
        border: 'none',
        borderRadius: 6,
        fontSize: 14,
        cursor: 'pointer',
      },
      content: {
        maxWidth: 900,
        margin: '0 auto',
        padding: '32px 24px',
      },
      section: {
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        padding: 24,
        marginBottom: 24,
      },
      sectionTitle: {
        fontSize: 18,
        fontWeight: 600,
        marginTop: 0,
        marginBottom: 20,
        color: textPrimary,
      },
      formGroup: {
        marginBottom: 16,
      },
      label: {
        display: 'block',
        fontSize: 14,
        color: textSecondary,
        marginBottom: 6,
      },
      hint: {
        fontSize: 12,
        color: isLightBackground ? '#9ca3af' : '#6b7280',
        marginBottom: 6,
      },
      input: {
        width: '100%',
        padding: '10px 14px',
        background: inputBg,
        border: `1px solid ${inputBorder}`,
        borderRadius: 6,
        color: textPrimary,
        fontSize: 14,
        boxSizing: 'border-box',
      },
      textarea: {
        width: '100%',
        padding: '10px 14px',
        background: inputBg,
        border: `1px solid ${inputBorder}`,
        borderRadius: 6,
        color: textPrimary,
        fontSize: 14,
        fontFamily: 'inherit',
        resize: 'vertical',
        boxSizing: 'border-box',
      },
      colorGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 16,
      },
      colorInputWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      },
      colorInput: {
        width: 40,
        height: 40,
        padding: 0,
        border: `1px solid ${inputBorder}`,
        borderRadius: 6,
        cursor: 'pointer',
        background: 'transparent',
      },
      colorValue: {
        flex: 1,
        padding: '8px 12px',
        background: inputBg,
        border: `1px solid ${inputBorder}`,
        borderRadius: 6,
        color: textPrimary,
        fontSize: 13,
        fontFamily: 'monospace',
      },
      checkbox: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
      },
      previewSection: {
        marginTop: 20,
        paddingTop: 20,
        borderTop: `1px solid ${cardBorder}`,
      },
      previewTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: textSecondary,
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      },
      previewBox: {
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${inputBorder}`,
        maxWidth: 200,
      },
      actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
      },
      cancelButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '12px 24px',
        background: 'transparent',
        border: `1px solid ${cardBorder}`,
        borderRadius: 6,
        color: textSecondary,
        cursor: 'pointer',
        fontSize: 14,
      },
      saveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '12px 24px',
        background: primaryColor,
        border: 'none',
        borderRadius: 6,
        color: '#fff',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 600,
      },
      banner: {
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      errorBanner: {
        background: isLightBackground ? '#fef2f2' : 'rgba(239, 68, 68, 0.1)',
        border: `1px solid ${isLightBackground ? '#fecaca' : 'rgba(239, 68, 68, 0.3)'}`,
        color: '#ef4444',
      },
      successBanner: {
        background: isLightBackground ? '#f0fdf4' : 'rgba(34, 197, 94, 0.1)',
        border: `1px solid ${isLightBackground ? '#bbf7d0' : 'rgba(34, 197, 94, 0.3)'}`,
        color: '#22c55e',
      },
      textPrimary,
      textSecondary,
    };
  }, [backgroundColor, effectiveTextColor, isLightBackground, primaryColor]);

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const payload = {
        id: tenant.id,
        name: tenant.name,
        key: tenant.key,
        branding: {
          companyName,
          logoUrl: logoUrl || undefined,
          primaryColor,
          accentColor,
          backgroundColor,
          textColor: textColorAuto ? undefined : textColor,
          headerGradient: headerGradient || undefined,
          buttonPrimaryBg,
          buttonTextColor,
        },
        interview_config: {
          intro_script: introScript,
          end_script: endScript,
          thank_you_heading: thankHeading,
          thank_you_body: thankBody,
        },
      };
      
      const response = await fetch(`${API_BASE}/tenants/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save');
      }
      
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#0d1117', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#9ca3af'
      }}>
        Loading configuration...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} style={styles.logoImage} />
          ) : (
            <h1 style={styles.logoText}>{companyName || tenantKey}</h1>
          )}
          <span style={styles.subtitle}>Configuration</span>
        </div>
        <button 
          onClick={() => navigate(getAdminPath())}
          style={styles.backButton}
        >
          <ArrowLeft size={16} />
          Admin Home
        </button>
      </div>

      <div style={styles.content}>
        {error && (
          <div style={{ ...styles.banner, ...styles.errorBanner }}>
            {error}
            <button 
              onClick={() => setError(null)} 
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        )}
        
        {success && (
          <div style={{ ...styles.banner, ...styles.successBanner }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSave}>
          {/* Branding Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Branding</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={styles.input}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Primary Color</label>
                <div style={styles.colorInputWrapper}>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={styles.colorInput}
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={styles.colorValue}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Button Background</label>
                <div style={styles.colorInputWrapper}>
                  <input
                    type="color"
                    value={buttonPrimaryBg}
                    onChange={(e) => setButtonPrimaryBg(e.target.value)}
                    style={styles.colorInput}
                  />
                  <input
                    type="text"
                    value={buttonPrimaryBg}
                    onChange={(e) => setButtonPrimaryBg(e.target.value)}
                    style={styles.colorValue}
                  />
                </div>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Button Text Color</label>
                <div style={styles.colorInputWrapper}>
                  <input
                    type="color"
                    value={buttonTextColor}
                    onChange={(e) => setButtonTextColor(e.target.value)}
                    style={styles.colorInput}
                  />
                  <input
                    type="text"
                    value={buttonTextColor}
                    onChange={(e) => setButtonTextColor(e.target.value)}
                    style={styles.colorValue}
                  />
                </div>
              </div>
            </div>

            {/* Interview Page Colors */}
            <div style={{ 
              background: isLightBackground ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)', 
              borderRadius: 8, 
              padding: 16, 
              marginTop: 16 
            }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, color: styles.textSecondary }}>
                Interview Page Colors
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16, alignItems: 'start' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Background Color</label>
                  <div style={styles.colorInputWrapper}>
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      style={styles.colorInput}
                    />
                    <input
                      type="text"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      style={styles.colorValue}
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Text Color
                    <label style={{ marginLeft: 12, fontWeight: 'normal' }}>
                      <input
                        type="checkbox"
                        checked={textColorAuto}
                        onChange={(e) => setTextColorAuto(e.target.checked)}
                        style={{ marginRight: 4 }}
                      />
                      Auto
                    </label>
                  </label>
                  <div style={styles.colorInputWrapper}>
                    <input
                      type="color"
                      value={textColorAuto ? autoTextColor : textColor}
                      onChange={(e) => { setTextColorAuto(false); setTextColor(e.target.value); }}
                      style={{ ...styles.colorInput, opacity: textColorAuto ? 0.5 : 1 }}
                      disabled={textColorAuto}
                    />
                    <input
                      type="text"
                      value={textColorAuto ? '(auto)' : textColor}
                      onChange={(e) => { setTextColorAuto(false); setTextColor(e.target.value); }}
                      style={{ ...styles.colorValue, opacity: textColorAuto ? 0.5 : 1 }}
                      disabled={textColorAuto}
                    />
                  </div>
                </div>

                {/* Live Preview */}
                <div style={styles.previewBox}>
                  <div style={{ 
                    padding: 8, 
                    fontSize: 10, 
                    color: '#6b7280', 
                    textAlign: 'center',
                    background: isLightBackground ? '#f3f4f6' : 'rgba(0,0,0,0.3)'
                  }}>
                    Live Preview
                  </div>
                  <div style={{ 
                    background: backgroundColor, 
                    padding: 16, 
                    textAlign: 'center' 
                  }}>
                    <div style={{ color: effectiveTextColor, fontWeight: 600, marginBottom: 4 }}>
                      Welcome!
                    </div>
                    <div style={{ color: effectiveTextColor, opacity: 0.7, fontSize: 12, marginBottom: 8 }}>
                      Interview text
                    </div>
                    <button style={{
                      padding: '6px 14px',
                      background: buttonPrimaryBg,
                      color: buttonTextColor,
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                    }}>
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ ...styles.formGroup, marginTop: 16 }}>
              <label style={styles.label}>Header Gradient (CSS)</label>
              <input
                type="text"
                value={headerGradient}
                onChange={(e) => setHeaderGradient(e.target.value)}
                placeholder="linear-gradient(135deg, #000000 0%, #1a1a1a 100%)"
                style={styles.input}
              />
            </div>
          </div>

          {/* Interview Scripts Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Interview Scripts</h2>
            <p style={styles.hint}>
              Available tokens: {'{greeting}'}, {'{candidate_first_name}'}, {'{role_title}'}, {'{company_name}'}
            </p>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Intro Script</label>
              <textarea
                value={introScript}
                onChange={(e) => setIntroScript(e.target.value)}
                rows={4}
                style={styles.textarea}
                placeholder="{greeting}, {candidate_first_name}. Thanks for taking the time..."
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>End Script</label>
              <textarea
                value={endScript}
                onChange={(e) => setEndScript(e.target.value)}
                rows={3}
                style={styles.textarea}
                placeholder="Thank you for completing the interview..."
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Thank You Heading</label>
                <input
                  type="text"
                  value={thankHeading}
                  onChange={(e) => setThankHeading(e.target.value)}
                  style={styles.input}
                  placeholder="Interview Complete!"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Thank You Body</label>
                <textarea
                  value={thankBody}
                  onChange={(e) => setThankBody(e.target.value)}
                  rows={2}
                  style={styles.textarea}
                  placeholder="Thank you for taking the time..."
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button
              type="button"
              onClick={() => navigate(getAdminPath())}
              style={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={styles.saveButton}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
